import { MESSAGE_TASKS, automationMessage, type AutomationTask } from "@rutamagica/contracts";
import { ArrowRight, CalendarClock, Check, Clock, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { Button, StatePanel } from "../../components/ui";
import { automationApi } from "../../data/crm-automation";
import { viewCache } from "../../data/view-cache";
import { workspaceApi } from "../../data/workspace";
import { formatDateTime } from "../../data/format";
import type { ServiceType } from "../../data/types";
import { groupTasksByDueDate } from "./automation-board";
import { mexicoMorning, NextActionDialog } from "./NextActionDialog";
import { ServiceTag } from "./ServiceTag";
/** Completing one of these asks for the next follow-up. */
const followupCategories = ["inquiry", "followup", "proposal"];
import {
  caseUrl,
  funnelUrl,
  serviceFromSlug,
  serviceNames,
  serviceSlugs,
} from "./shared";

const services: ServiceType[] = ["balloon", "tour", "school"];
const groups = [
  { key: "Vencidas", label: "Atrasado", open: true },
  { key: "Hoy", label: "Hoy", open: true },
  { key: "Próximas", label: "Próximos días", open: false },
  { key: "Sin fecha", label: "Sin fecha", open: false },
] as const;

const taskTime = (t: AutomationTask) =>
  Date.parse((t.status === "snoozed" ? t.snoozedUntil : t.dueAt) ?? t.updatedAt) || 0;
/** Newest first, except upcoming work, where the closest date matters most. */
export function sortTodayGroups(grouped: Record<string, AutomationTask[]>) {
  for (const [key, items] of Object.entries(grouped))
    items.sort((a, b) =>
      key === "Próximas" ? taskTime(a) - taskTime(b) : taskTime(b) - taskTime(a),
    );
  return grouped;
}

/**
 * The ready WhatsApp message for the task; the case opens with it in the chat box to review and send.
 * Amounts and dates come from the booking so the text matches what WooCommerce reports.
 */
export async function prepareTaskMessage(task: AutomationTask) {
  const dossier = await workspaceApi.caseDetail(task.caseRef.kind, task.caseRef.id, { deferQuotes: true });
  const booking =
    dossier.bookings.find((b) => Number((b.payment_summary as Record<string, unknown> | null)?.balance_due) > 0) ??
    dossier.bookings[0];
  const summary = (booking?.payment_summary ?? {}) as Record<string, unknown>;
  const body = automationMessage(task.ruleKey, {
    customerName: dossier.customer.name ?? task.customerName ?? "",
    service: String(booking?.service_type ?? dossier.service ?? task.service ?? ""),
    serviceDate: booking?.booking_date ? String(booking.booking_date).slice(0, 10) : dossier.opportunity?.requested_date ? String(dossier.opportunity.requested_date).slice(0, 10) : null,
    balance: Number(summary.balance_due ?? 0),
    currency: String(booking?.currency ?? "MXN"),
    dueDate: typeof summary.balance_due_at_local === "string" ? summary.balance_due_at_local.slice(0, 10) : null,
  });
  if (!body) throw new Error("Esta tarea no tiene mensaje para el cliente.");
  return body;
}

/** 09:00 tomorrow in Mexico City. */
export const tomorrowMorning = (now = new Date()) => mexicoMorning(1, now);

const todayCache = viewCache<{ tasks: AutomationTask[]; truncated: boolean }>();

/** The single "what do I do now" list: every pending follow-up across services, ordered by date. */
export function TodayTasks() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("crm.write");
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const service = serviceFromSlug(params.get("servicio"));
  const [tasks, setTasks] = useState<AutomationTask[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState("");
  const [revision, setRevision] = useState(0);
  const [schedule, setSchedule] = useState<{
    opportunityId: string | null;
    customerName: string;
    previous: "completed" | "cancelled";
  } | null>(null);
  const askNext = async (task: AutomationTask, previous: "completed" | "cancelled") => {
    const opportunityId =
      task.caseRef.kind === "opportunity"
        ? task.caseRef.id
        : await automationApi
            .detail(task.caseRef)
            .then((c) => c.commercial.opportunityId)
            .catch(() => null);
    if (opportunityId || previous === "cancelled")
      setSchedule({ opportunityId, customerName: task.customerName ?? task.caseLabel, previous });
  };
  useEffect(() => {
    let active = true;
    setError("");
    const query = (status: string) => {
      const q = new URLSearchParams({ status, per_page: "100" });
      if (service) q.set("service", service);
      return automationApi.tasks(q);
    };
    const cacheKey = service ?? "all";
    const cached = todayCache.get(cacheKey);
    if (cached) {
      setTasks(cached.tasks);
      setTruncated(cached.truncated);
    }
    Promise.all([query("pending"), query("snoozed")])
      .then(([pending, snoozed]) => {
        const next = {
          tasks: [...pending.data, ...snoozed.data],
          truncated:
            pending.meta.total > pending.data.length ||
            snoozed.meta.total > snoozed.data.length,
        };
        todayCache.set(cacheKey, next);
        if (!active) return;
        setTasks(next.tasks);
        setTruncated(next.truncated);
      })
      .catch((e) => {
        if (active)
          setError(
            e instanceof Error ? e.message : "No pudimos cargar tus pendientes.",
          );
      });
    return () => {
      active = false;
    };
  }, [service, revision]);
  const navigate = useNavigate();
  const canRemind = canWrite && hasPermission("finance.read");
  const remind = async (task: AutomationTask) => {
    setBusy(task.id);
    setFeedback("");
    try {
      const reply = await prepareTaskMessage(task);
      navigate(caseUrl(task.caseRef.kind, task.caseRef.id, location.pathname + location.search), { state: { reply } });
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "No pudimos preparar el mensaje.");
    } finally {
      setBusy("");
    }
  };
  const act = async (task: AutomationTask, action: "complete" | "snooze") => {
    setBusy(task.id);
    setFeedback("");
    try {
      await automationApi.task(task.id, {
        commandId: crypto.randomUUID(),
        expectedVersion: task.version,
        action,
        ...(action === "snooze" ? { snoozedUntil: tomorrowMorning() } : {}),
      });
      setTasks((list) => list?.filter((t) => t.id !== task.id) ?? null);
      setFeedback(
        action === "complete"
          ? `Hecho: ${task.title}.`
          : `${task.title} pasó a mañana a las 9:00.`,
      );
      if (action === "complete" && followupCategories.includes(task.category))
        void askNext(task, "completed");
    } catch (e) {
      setFeedback(
        e instanceof Error ? e.message : "No pudimos actualizar el pendiente.",
      );
      setRevision((x) => x + 1);
    } finally {
      setBusy("");
    }
  };
  const setService = (value?: ServiceType) => {
    const next = new URLSearchParams(params);
    if (value) next.set("servicio", serviceSlugs[value]);
    else next.delete("servicio");
    setParams(next, { replace: true });
  };
  const grouped = sortTodayGroups(groupTasksByDueDate(tasks ?? []));
  const returnTo = location.pathname + location.search;
  return (
    <section className="workspace-card today-tasks" aria-label="Tus pendientes">
      <header>
        <div>
          <h2>Tus pendientes</h2>
        </div>
      </header>
      <div className="today-counts">
        {groups.slice(0, 3).map((g) => (
          <div key={g.key} className={`today-count today-count--${g.key === "Vencidas" ? "late" : g.key === "Hoy" ? "today" : "next"}`}>
            <strong>{tasks ? grouped[g.key]!.length : "—"}</strong>
            <span>{g.label}</span>
          </div>
        ))}
      </div>
      <div className="funnel-chips" role="group" aria-label="Servicio">
        {[undefined, ...services].map((item) => (
          <button
            type="button"
            key={item ?? "all"}
            className="funnel-chip"
            aria-pressed={item === service}
            onClick={() => setService(item)}
          >
            {item ? serviceNames[item] : "Todos"}
          </button>
        ))}
      </div>
      {feedback && (
        <p className="quote-feedback" role="status">
          {feedback}
        </p>
      )}
      {error ? (
        <StatePanel
          state="error"
          description={error}
          onRetry={() => setRevision((x) => x + 1)}
        />
      ) : !tasks ? (
        <StatePanel state="loading" title="Cargando tus pendientes" />
      ) : !tasks.length ? (
        <div className="home-clear-state">
          <Check size={28} />
          <strong>No tienes pendientes</strong>
          <p>
            Todo al día. Revisa el <Link to={funnelUrl(service)}>Embudo</Link>{" "}
            o registra una nueva consulta.
          </p>
        </div>
      ) : (
        groups.map((g) => {
          const items = grouped[g.key]!;
          if (!items.length) return null;
          const list = items.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              late={g.key === "Vencidas"}
              returnTo={returnTo}
              disabled={!canWrite || busy === t.id}
              onAct={act}
              onSchedule={(task) => void askNext(task, "cancelled")}
              onRemind={canRemind ? (task) => void remind(task) : undefined}
            />
          ));
          return g.open ? (
            <div key={g.key} className="today-group">
              <h3>
                {g.label} <small>{items.length}</small>
              </h3>
              {list}
            </div>
          ) : (
            <details key={g.key} className="today-group">
              <summary>
                {g.label} <small>{items.length}</small>
              </summary>
              {list}
            </details>
          );
        })
      )}
      <NextActionDialog
        open={schedule !== null}
        opportunityId={schedule?.opportunityId ?? null}
        customerName={schedule?.customerName ?? ""}
        previous={schedule?.previous ?? "cancelled"}
        onClose={() => setSchedule(null)}
        onSaved={() => {
          setFeedback("Próxima acción agendada.");
          setRevision((x) => x + 1);
        }}
      />
      {truncated && (
        <p className="automation-meta">
          Se muestran los primeros 100 pendientes. Filtra por servicio para ver
          el resto.
        </p>
      )}
    </section>
  );
}

function TaskRow({
  task: t,
  late,
  returnTo,
  disabled,
  onAct,
  onSchedule,
  onRemind,
}: {
  task: AutomationTask;
  late: boolean;
  returnTo: string;
  disabled: boolean;
  onAct: (task: AutomationTask, action: "complete" | "snooze") => void;
  onSchedule: (task: AutomationTask) => void;
  onRemind?: ((task: AutomationTask) => void) | undefined;
}) {
  const when = t.status === "snoozed" ? t.snoozedUntil : t.dueAt;
  return (
    <article className="today-task">
      <div>
        <strong>{t.customerName ?? t.caseLabel}</strong>
        <span>{t.title}</span>
        <small className={late ? "text-danger" : ""}>
          <ServiceTag service={t.service} />
          {when ? ` ${formatDateTime(when)}` : " Sin fecha"}
          {t.status === "snoozed" ? " · pospuesto" : ""}
        </small>
      </div>
      <div className="today-task-actions">
        <Link
          className="button button--secondary"
          to={caseUrl(t.caseRef.kind, t.caseRef.id, returnTo)}
          aria-label={`Abrir expediente de ${t.customerName ?? t.caseLabel}`}
        >
          Abrir <ArrowRight size={16} />
        </Link>
        {!disabled && t.ruleKey === "no_next_followup" ? (
          <Button
            variant="secondary"
            icon={CalendarClock}
            onClick={() => onSchedule(t)}
            aria-label={`Agendar próxima acción de ${t.customerName ?? t.caseLabel}`}
          >
            Agendar
          </Button>
        ) : !disabled && (
          <>
            {onRemind && (MESSAGE_TASKS as readonly string[]).includes(t.ruleKey) && (
              <Button
                icon={MessageCircle}
                onClick={() => onRemind(t)}
                aria-label={`Preparar mensaje de WhatsApp para ${t.customerName ?? t.caseLabel}`}
              >
                Preparar WhatsApp
              </Button>
            )}
            <Button
              variant="secondary"
              icon={Check}
              onClick={() => onAct(t, "complete")}
              aria-label={`Marcar como hecho: ${t.title}`}
            >
              Hecho
            </Button>
            <Button
              variant="quiet"
              icon={Clock}
              onClick={() => onAct(t, "snooze")}
              aria-label={`Pasar a mañana: ${t.title}`}
            >
              Mañana
            </Button>
          </>
        )}
      </div>
    </article>
  );
}
